import { useLightMode } from '@/hooks/useLightMode'

import { Box, Text } from '@chakra-ui/react'

import {
  Text as DarkText,
  StatusPill,
  TableCellProps,
  UserAvatar,
} from '@hivewatch/dark'

import { formatDateTime } from '@/utils/formatDateTime'
import { removeUnderscoreAndCapitalize } from '@/utils/formats'

import { SOPsRowDataType } from '../types/types'
import { FacilitiesCell } from './FacilitiesCell'
import { SOPsMenuButton } from './SOPsMenuButton'

export interface SOPsColumnIProps {
  key: string
  header: string
  style: Omit<TableCellProps, 'children'>
  sortColumn?: string
  cell: (row: SOPsRowDataType) => React.ReactNode
}

export const useColumns = (options?: {
  useMonoDate?: boolean
  useCompactLayout?: boolean
}): SOPsColumnIProps[] => {
  const mode = useLightMode()
  const prefix = mode === 'light' ? 'light.' : ''
  const isMonoDate = options?.useMonoDate ?? false
  const isCompact = options?.useCompactLayout ?? false

  return [
    {
      key: 'date',
      header: 'Date',
      style: isCompact
        ? { width: '200px' }
        : { minWidth: '150px', maxWidth: '200px', flexGrow: 1 },
      sortColumn: 'date',
      cell: (row) =>
        isMonoDate ? (
          <DarkText
            color={`${prefix}text.secondary`}
            data-testid='sopsPage_table_dateCell'
            fontFamily="'Soehne Mono', monospace"
            fontSize='12px'
            letterSpacing='1px'
            lineHeight='20px'
            truncate
          >
            {formatDateTime(row.date, 'HH:mm | MM/dd/yyyy')}
          </DarkText>
        ) : (
          <Text
            color={`text.secondary.${mode}`}
            data-testid='sopsPage_table_dateCell'
            isTruncated
          >
            {formatDateTime(row.date, 'HH:mm | MM/dd/yyyy')}
          </Text>
        ),
    },
    {
      key: 'name',
      header: 'SOP Name',
      style: isCompact
        ? { minWidth: '80px', maxWidth: '220px', flexGrow: 1 }
        : { minWidth: '200px', maxWidth: '450px', flexGrow: 1 },
      sortColumn: 'name',
      cell: (row) => (
        <Text
          color={`text.primary.${mode}`}
          data-testid='sopsPage_table_nameCell'
          isTruncated
        >
          {row.name}
        </Text>
      ),
    },
    {
      key: 'creator',
      header: 'Creator',
      style: isCompact
        ? { width: '100px' }
        : { minWidth: '150px', maxWidth: '250px', flexGrow: 1 },
      cell: (row) => {
        const fullName = row.creator
          ? `${row.creator.firstName} ${row.creator.lastName}`
          : 'Unknown'
        return <UserAvatar colorScheme={mode} name={fullName} />
      },
    },
    {
      key: 'type',
      header: 'Assigned To',
      style: { minWidth: '150px', maxWidth: '250px', flexGrow: 1 },
      cell: (row) => (
        <StatusPill
          colorScheme={mode}
          label={removeUnderscoreAndCapitalize(row.type)}
          variant='incomplete'
        />
      ),
    },
    {
      key: 'facilities',
      header: 'Facilities',
      style: { minWidth: '200px', maxWidth: '596px', flexGrow: 1 },
      cell: (row) => <FacilitiesCell facilities={row.facilities.facilities} />,
    },
    {
      key: 'actions',
      header: '',
      style: { width: '64px', align: 'center' },
      cell: (row) => (
        <Box onClick={(e) => e.stopPropagation()}>
          <SOPsMenuButton
            onDeleteClick={row.facilities.onRemove}
            onEditClick={row.facilities.onEdit}
          />
        </Box>
      ),
    },
  ]
}
